import * as React from "react";
import { usePathname, useNavigate as useRouter, useSearch as useSearchParams, RouterLink as Link } from "../../router";
import {
  Calendar as CalendarIcon,
  Check,
  CircleDashed,
  ClipboardCheck,
  FolderOpen,
  Kanban,
  Layers,
  List,
  UserRound,
} from "../../icons";

import { Calendar } from "../../calendar";
import { cn } from "../../utils";
import { INTENT_CLASSES } from "../../tokens";
import {
  STATUS_LABEL,
  STATUS_ORDER,
  type TaskStatus,
  type ViewScope,
} from "../../types/projects";
import { FilterBar } from "../filter-bar";
import { DisplayMenu, FilterMenu, type FilterField } from "../view-menu";
import { StatusIcon } from "./status-icon";
import {
  OPEN_STATUSES,
  SECTION_PRESETS,
  STATUS_SEGMENTS,
  type TaskRange,
  type TaskSection,
  type TaskView,
} from "./tasks-state";

const RANGE_LABEL: Record<TaskRange, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  all: "All time",
  custom: "Custom range",
};

export interface TasksHeaderProps {
  scope: ViewScope;
  /** Distinct assignees with active tasks. */
  assignees: string[];
  /** Workspace-only: list of projects for the Project filter category. */
  projects: { slug: string; name: string }[];
  section: TaskSection;
  view: TaskView;
  range: TaskRange;
  from?: string;
  to?: string;
  /** When non-empty, overrides `section`'s status preset. */
  statuses: TaskStatus[];
  assignee?: string;
  projectSlug?: string;
  showEmpty: boolean;
  /**
   * Optional primary action rendered at the right edge of the header — the
   * shell passes its "+ New" (task/goal) control here. Kept as a slot so this
   * block stays framework-agnostic (no Next server-action imports).
   */
  createSlot?: React.ReactNode;
  /**
   * Per-status task counts for the current scope. When provided, the left side
   * becomes the **segmented status filter** (Open · In progress · Needs review ·
   * Done) — compact count-pills that double as the status tabs, replacing the
   * All/Active/Backlog preset pills. The active pill is the current `statuses`
   * filter. This is the integrated-queue treatment (the stat bar folded into the
   * header); the same `counts` the standalone `IssuesDashboard` strip used.
   */
  counts?: Record<TaskStatus, number>;
  /**
   * Render the header as the flush caption of a wrapping `<Card>` (a bottom
   * hairline + `px-3 py-2`), so it reads as one surface with the task list
   * beneath it. Default false → the bare standalone header bar.
   */
  inCard?: boolean;
  /**
   * Which parts of the header to render, so the cluster can be hoisted out of the
   * body. `"all"` (default) = status filter (left) + Filter/View/New cluster
   * (right); `"status"` = ONLY the status filter (the list caption when the
   * cluster is hoisted); `"controls"` = ONLY the Filter/New cluster (the hoisted
   * title-row toolbar).
   */
  parts?: "all" | "status" | "controls";
}

// STATUS_SEGMENTS + OPEN_STATUSES are the shared Issues-status vocabulary, hoisted
// into the server-safe `tasks-state.ts` so the mode header producer and its pill
// nav read the SAME segments this header does (imported above).

const PILLS: { id: TaskSection; label: string }[] = [
  { id: "all", label: "All issues" },
  { id: "active", label: "Active" },
  { id: "backlog", label: "Backlog" },
];

/**
 * Linear-style header for the task list / board pages. Hosts the
 * section-preset pills (left), and the Filter / View / side-panel icons
 * (right). URL state is the single source of truth — every control writes
 * to the current pathname's query string via `router.replace`.
 */
export function TasksHeader(props: TasksHeaderProps) {
  const {
    scope,
    section,
    view,
    range,
    statuses,
    assignee,
    projectSlug,
    showEmpty,
    counts,
    inCard = false,
    parts = "all",
  } = props;

  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();

  const buildUrl = React.useCallback(
    (updates: Record<string, string | null | undefined>): string => {
      const next = new URLSearchParams(sp.toString());
      for (const [key, value] of Object.entries(updates)) {
        if (value == null || value === "") next.delete(key);
        else next.set(key, value);
      }
      const qs = next.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [pathname, sp],
  );

  const replace = React.useCallback(
    (updates: Record<string, string | null | undefined>) => {
      router.replace(buildUrl(updates));
    },
    [router, buildUrl],
  );

  // Highlight a pill only when its preset is the active one — if `?status=`
  // is overriding, no pill lights up (the user has a custom set).
  const pillActive: TaskSection | null = statuses.length > 0 ? null : section;

  // Segmented status filter (integrated treatment): active when the current
  // explicit `?status=` set exactly equals a segment's status set.
  const curStatuses = new Set(statuses);
  const segActive = (segStatuses: TaskStatus[]) =>
    segStatuses.length === curStatuses.size &&
    segStatuses.every((s) => curStatuses.has(s));

  const showStatus = parts !== "controls";
  const showControls = parts !== "status";

  // The filter FIELDS, as data. Everything the old bespoke popover expressed as a
  // hand-rolled category list + back button is now four typed entries; the menu
  // owns the hover submenus, the per-field search, and the applied-count dot.
  const filterFields: FilterField[] = [
    {
      kind: "multi",
      id: "status",
      label: "Status",
      icon: CircleDashed,
      options: STATUS_ORDER.map((s) => ({
        value: s,
        label: STATUS_LABEL[s],
        mark: <StatusIcon status={s} />,
        hint: counts ? String(counts[s] ?? 0) : undefined,
      })),
      values: statuses,
      // The open buckets are what every Issues surface lands on (the mode mount
      // injects exactly this set), so sitting on it is not a filter choice.
      defaultValues: OPEN_STATUSES,
      // Picking explicit statuses supersedes the section preset, so the preset
      // has to be cleared or the two would disagree about what's on screen.
      onChange: (next) =>
        replace({
          status: next.length > 0 ? next.join(",") : null,
          section: null,
        }),
    },
    {
      kind: "single",
      id: "assignee",
      label: "Assignee",
      icon: UserRound,
      clearLabel: "Anyone",
      options: props.assignees.map((a) => ({ value: a, label: a })),
      value: assignee ?? null,
      onChange: (next) => replace({ assignee: next }),
    },
    {
      kind: "single",
      id: "project",
      label: "Project",
      icon: FolderOpen,
      clearLabel: "Any project",
      startsGroup: true,
      // Hidden only when the scope is STRUCTURALLY fixed — a project-route
      // mount. Not when the user picked a project here: the page flips `scope`
      // to `project` as soon as `?project=` resolves, which would otherwise
      // vanish the very filter they just set, leaving no way to see or remove
      // it. `projectSlug` is the filter's own value, so it keeps the field.
      visible: scope.kind === "workspace" || projectSlug != null,
      options: props.projects.map((p) => ({ value: p.slug, label: p.name })),
      value: projectSlug ?? null,
      onChange: (next) => replace({ project: next }),
    },
    {
      // The one field the generic kinds can't express: a preset list that can
      // fall through to a two-month range calendar. `custom` exists for exactly
      // this, so the date picker survives the migration unchanged.
      kind: "custom",
      id: "dates",
      label: "Dates",
      icon: CalendarIcon,
      startsGroup: true,
      // "All time" is the absence of a date filter; anything else narrows.
      active: range !== "all",
      summary: range === "all" ? undefined : RANGE_LABEL[range],
      onClear: () => replace({ range: "all", from: null, to: null }),
      widthPx: 300,
      render: ({ close }) => (
        <DatesFilter
          range={range}
          from={props.from}
          to={props.to}
          onApply={(updates) => {
            replace(updates);
            close();
          }}
        />
      ),
    },
  ];

  // The right-side control cluster (Filter + optional display menu + create slot) —
  // the same set whether it sits inline in this header (`parts="all"`) or is hoisted
  // into the title-row `<SectionActions>` (`parts="controls"`, returned bare below).
  const controls = showControls ? (
    <div className="flex items-center gap-1">
      <FilterMenu
        fields={filterFields}
        onReset={() =>
          replace({
            status: null,
            section: null,
            assignee: null,
            project: null,
            range: null,
            from: null,
            to: null,
          })
        }
      />
      {/* The view switch + show-empty toggle is meaningful only on the standalone
          multi-view page. The integrated treatment (`inCard`) is a flat
          single-view surface mounted under the mode shell, where `?view=` is a
          routing param (stripped before the page sees it) — so the menu can't
          switch views there. Hide it rather than show a dead control. Hoisted
          (`parts="controls"`) the display menu lives in IssuesSubNav, which owns
          the path-based List/Board/Calendar switch. */}
      {parts === "all" && !inCard ? (
        <DisplayMenu
          views={{
            options: [
              { value: "list", label: "List", icon: List },
              { value: "board", label: "Board", icon: Kanban },
              { value: "review", label: "Review", icon: ClipboardCheck },
            ],
            value: view,
            onChange: (v) => replace({ view: v === "list" ? null : v }),
          }}
          sections={[
            {
              id: "list",
              controls: [
                {
                  kind: "select",
                  id: "grouping",
                  label: "Grouping",
                  // Status grouping is what the list actually does; the row is
                  // here to state it, not to pretend it's configurable yet.
                  options: [{ value: "status", label: "Status" }],
                  value: "status",
                  onChange: () => {},
                  disabled: true,
                },
                {
                  kind: "switch",
                  id: "empty",
                  label: "Show empty groups",
                  checked: showEmpty,
                  onChange: (on) => replace({ empty: on ? "1" : null }),
                },
              ],
            },
          ]}
        />
      ) : null}
      {props.createSlot ?? null}
    </div>
  ) : null;

  // Hoisted into the title-row `<SectionActions>`: render JUST the control cluster,
  // no header wrapper or status chrome (the status filter stays as the list caption).
  if (parts === "controls") return controls;

  // The applied-filter read-out. It belongs to the body, not the hoisted control
  // cluster: it describes the rows, so it sits with them. Renders nothing when
  // no filter is applied.
  const filterBar = showStatus ? (
    <FilterBar
      fields={filterFields}
      onReset={() =>
        replace({
          status: null,
          section: null,
          assignee: null,
          project: null,
          range: null,
          from: null,
          to: null,
        })
      }
      className={inCard ? undefined : "rounded-md border border-border/60"}
    />
  ) : null;

  return (
    <>
    {filterBar}
    <header
      data-slot="tasks-header"
      className={cn(
        "flex items-center gap-3",
        showStatus && showControls
          ? "justify-between"
          : showControls
            ? "justify-end"
            : null,
        inCard && "border-b border-border px-3 py-2",
      )}
    >
        {showStatus && counts ? (
          <div
            role="tablist"
            aria-label="Filter by status"
            className="-mx-1 flex items-center gap-1 overflow-x-auto px-1"
          >
            {STATUS_SEGMENTS.map((seg) => {
              const on = segActive(seg.statuses);
              const total = seg.statuses.reduce(
                (n, s) => n + (counts[s] ?? 0),
                0,
              );
              return (
                <Link
                  key={seg.key}
                  href={buildUrl({
                    status: seg.statuses.join(","),
                    range: "all",
                    section: null,
                  })}
                  role="tab"
                  aria-selected={on}
                  className={cn(
                    "inline-flex h-7 shrink-0 items-center gap-2 rounded-md px-2.5 type-body transition-colors",
                    on
                      ? "bg-surface-3 text-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn("size-2 rounded-full", INTENT_CLASSES[seg.intent].dot)}
                  />
                  {seg.label}
                  <span className="type-subtext tabular-nums text-muted-foreground">
                    {total}
                  </span>
                </Link>
              );
            })}
          </div>
        ) : showStatus ? (
          <div className="flex items-center gap-1">
            {PILLS.map((p) => (
              <Link
                key={p.id}
                href={buildUrl({
                  section: p.id === "active" ? null : p.id,
                  status: null,
                })}
                className={cn(
                  "inline-flex h-7 items-center rounded-md px-2.5 type-body transition-colors",
                  pillActive === p.id
                    ? "bg-surface-3 text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {p.label}
              </Link>
            ))}
            <span
              aria-hidden
              className="ml-1 inline-flex items-center pl-1 text-muted-foreground/60"
              title="Grouped by status"
            >
              <Layers className="size-3.5" />
            </span>
          </div>
        ) : null}

        {controls}
    </header>
    </>
  );
}


/** The one filter body the generic field kinds can't express: four presets that
 *  fall through to a two-month range calendar. Mounted as the Dates field's
 *  `custom` render, so it kept its behaviour across the migration — only its row
 *  grammar changed to match the surrounding menu. */
function DatesFilter({
  range,
  from,
  to,
  onApply,
}: {
  range: TaskRange;
  from?: string;
  to?: string;
  onApply: (updates: Record<string, string | null | undefined>) => void;
}) {
  const [customMode, setCustomMode] = React.useState(range === "custom");
  const presets: TaskRange[] = ["7d", "30d", "90d", "all"];
  // Mirrors the menu's own row geometry (view-menu.tsx `ROW`) — these are plain
  // buttons, not Radix menu items, so they can't inherit it.
  const ROW =
    "relative flex w-full cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-left type-body hover:bg-foreground/[0.06]";
  return (
    <div className="flex flex-col">
      <ul>
        {presets.map((r) => (
          <li key={r}>
            <button
              type="button"
              onClick={() =>
                onApply({
                  range: r === "7d" ? null : r,
                  from: null,
                  to: null,
                })
              }
              className={ROW}
            >
              <span>{RANGE_LABEL[r]}</span>
              {!customMode && range === r ? (
                <Check className="ml-auto size-3.5" />
              ) : null}
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={() => setCustomMode(true)}
            className={ROW}
          >
            <CalendarIcon className="size-3.5 text-muted-foreground" />
            Custom…
            {customMode || range === "custom" ? (
              <Check className="ml-auto size-3.5" />
            ) : null}
          </button>
        </li>
      </ul>
      {customMode ? (
        <div className="border-t border-border/60 p-2">
          <Calendar
            mode="range"
            selected={{
              from: from ? new Date(from) : undefined,
              to: to ? new Date(to) : undefined,
            }}
            onSelect={(r) => {
              if (r?.from && r?.to) {
                onApply({
                  range: "custom",
                  from: r.from.toISOString().slice(0, 10),
                  to: r.to.toISOString().slice(0, 10),
                });
              }
            }}
            disabled={{ after: new Date() }}
          />
        </div>
      ) : null}
    </div>
  );
}
